import React from 'react';
import ReactMarkdown from 'react-markdown';
import { fallbackMarkdownTables } from '../../utils/markdownTables';
import { asChatText } from '../../utils/streamingReplyDisplay';

/**
 * MarkdownMessage — markdown renderer for assistant chat messages.
 *
 * Replaces the legacy plaintext-with-<br> renderer so LLM output (bold,
 * lists, headings) displays properly instead of showing literal asterisks.
 *
 * Citations: the FMA citation syntax `[messageId]` must keep working inside
 * markdown blocks (including list items), so a small remark plugin rewrites
 * citation tokens in text nodes into `#cite-` links, which the `a` component
 * override renders as the existing clickable citation span.
 */

const CITATION_PATTERN = /\[([a-zA-Z0-9]+)\]/g;

// remark plugin: split mdast text nodes on [id] tokens into citation links.
function remarkCitations() {
  const visit = (node) => {
    if (!node.children) return;
    const next = [];
    for (const child of node.children) {
      if (child.type !== 'text') {
        visit(child);
        next.push(child);
        continue;
      }
      const value = child.value;
      let last = 0;
      let m;
      let found = false;
      CITATION_PATTERN.lastIndex = 0;
      while ((m = CITATION_PATTERN.exec(value)) !== null) {
        found = true;
        if (m.index > last) {
          next.push({ type: 'text', value: value.slice(last, m.index) });
        }
        next.push({
          type: 'link',
          url: `#cite-${m[1]}`,
          children: [{ type: 'text', value: m[1] }]
        });
        last = m.index + m[0].length;
      }
      if (!found) {
        next.push(child);
        continue;
      }
      if (last < value.length) {
        next.push({ type: 'text', value: value.slice(last) });
      }
    }
    node.children = next;
  };
  return (tree) => visit(tree);
}

// CommonMark collapses single newlines into spaces; LLM output relies on them
// as visual line breaks. Convert lone newlines to markdown hard breaks
// ("  \n") without regex lookbehind (Safari <16.4 breaks at parse time).
function preserveLineBreaks(content) {
  const text = typeof content === 'string' ? content : '';
  return text
    .split('\n\n')
    .map((block) => block.replace(/\n/g, '  \n'))
    .join('\n\n');
}

function MarkdownMessage({ content, citationMap = {}, onCitationClick }) {
  const text = asChatText(content);
  if (!text) return null;

  const components = {
    a: ({ href, children }) => {
      if (href && href.startsWith('#cite-')) {
        const messageId = href.slice('#cite-'.length);
        const citationData = citationMap[messageId];
        const importSource = citationData?.kind === 'import_chunk';
        return (
          <span
            className="chat-message__citation"
            onClick={() => onCitationClick?.(messageId)}
            title={
              importSource
                ? 'Jump to imported source'
                : citationData
                  ? `Jump to message: ${citationData.preview}`
                  : 'Referenced message'
            }
          >
            [{messageId}]
          </span>
        );
      }
      const isExternal = /^https?:\/\//.test(href || '');
      return (
        <a
          href={href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
        >
          {children}
        </a>
      );
    }
  };

  return (
    <ReactMarkdown remarkPlugins={[remarkCitations]} components={components}>
      {preserveLineBreaks(fallbackMarkdownTables(text))}
    </ReactMarkdown>
  );
}

export default MarkdownMessage;
